import { useForm, useFormContext } from 'react-hook-form';

import { MAP_LAYER_FACTS, ThreatType } from '@/api/rest/resources/types/fact';
import { IndicatorProductSubscriptionConfiguration } from '@/api/rest/resources/types/IndicatorProduct';

import { LAYER_OPTIONS } from '../constants';

export const DEFAULT_LAYER_OPACITY = 40;

export const getDefaultOpacityByLayer = (): Record<MAP_LAYER_FACTS, number> => {
  const defaults = Object.values(MAP_LAYER_FACTS).reduce(
    (acc, layer) => {
      acc[`${layer}`] = DEFAULT_LAYER_OPACITY;
      return acc;
    },
    {} as Record<MAP_LAYER_FACTS, number>,
  );

  return {
    ...defaults,
    [MAP_LAYER_FACTS.gbif_species_occurrence]: 100,
    [MAP_LAYER_FACTS.habitat_intactness_raster_urls]: 80,
    [MAP_LAYER_FACTS.ecosystem_integrity_index_map_url]: 80,
    [MAP_LAYER_FACTS.opendata_protected_areas]: 80,
    [MAP_LAYER_FACTS.iucn_protected_areas]: 80,
    [MAP_LAYER_FACTS.in_situ_map_layer]: 100,
  };
};

export const MAP_BACKGROUND = {
  satellite: 'satellite',
  light: 'light',
} as const;
export type MapBackground = (typeof MAP_BACKGROUND)[keyof typeof MAP_BACKGROUND];
export const DEFAULT_MAP_BACKGROUND: MapBackground = MAP_BACKGROUND.satellite;

export const PLOT_OVERVIEW_SHAPE = {
  polygon: 'polygon',
  border: 'border',
} as const;
export type PlotOverviewShape = (typeof PLOT_OVERVIEW_SHAPE)[keyof typeof PLOT_OVERVIEW_SHAPE];
export const DEFAULT_PLOT_OVERVIEW_SHAPE: PlotOverviewShape = PLOT_OVERVIEW_SHAPE.polygon;

export type ControlsFormFieldValues = {
  year: string;
  layers: string[];
  radius: string;
  habitatIntactnessThreats: ThreatType[];
  layerOpacity: Record<MAP_LAYER_FACTS, number>;
  mapBackground: MapBackground;
  plotOverviewShape: PlotOverviewShape;
  // In-situ tool media map related fields
  inSituLayerConfiguration: IndicatorProductSubscriptionConfiguration[];
};
export const useControlsForm = useForm<ControlsFormFieldValues>;
export const useControlsContext = useFormContext<ControlsFormFieldValues>;

// In-situ layers are stored as `${MAP_LAYER_FACTS.in_situ_map_layer}-${configurationId}` so that multiple  media map configurations can be selected at once
export const isLayerValueSelected = (layers: string[] | undefined, value: string) =>
  (layers ?? []).some((layer) => layer === value || layer.startsWith(value));

export const useMapSelectedLayer = () => {
  const controlsForm = useControlsContext();
  const layers = controlsForm.watch('layers');
  return LAYER_OPTIONS.filter((opt) => isLayerValueSelected(layers, opt.value)) ?? [];
};
